import React from "react";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import { IMAGES } from "../lib/images";

const TIMELINE = [
  { year: "2022", title: "Founding Vision", body: "Colton Elliott establishes Elliott International Development LLC with a singular thesis — landmark vertical urbanism for Tampa." },
  { year: "2024", title: "Capital Formation", body: "Initial capital partnerships formed with family-office and institutional partners committed to long-duration assets." },
  { year: "2025", title: "Elliott Towers Unveiled", body: "EID reveals the Elliott Towers twin-tower mixed-use vision for Downtown Tampa." },
  { year: "2026", title: "Pipeline Expansion", body: "Pipeline grows to include Bayshore, Channel District and Tampa Bay Hospitality opportunities." },
  { year: "2032", title: "Topping Out", body: "Elliott Towers tops out as Tampa's tallest and most ambitious mixed-use landmark." },
];

const VALUES = [
  { n: "I", t: "Architectural Ambition", b: "We pursue projects that elevate the architectural language of every city we build in." },
  { n: "II", t: "Institutional Discipline", b: "We approach capital, design, and execution with the rigor of a Fortune 500 developer." },
  { n: "III", t: "Public Benefit", b: "Every development is engineered to contribute to the urban realm — civic space, public access, transit." },
  { n: "IV", t: "Generational Quality", b: "We build assets meant to remain relevant a century after delivery." },
];

export default function AboutPage() {
  return (
    <Layout>
      <SEO title="About" description="Elliott International Development LLC — founded by Colton Elliott. Tampa-based institutional developer of landmark mixed-use towers and ultra-luxury residences." path="/about" />
      {/* HERO */}
      <section className="relative pt-40 pb-24 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">— About</div>
            <div className="overline mt-2 text-foreground/50">Elliott International Development</div>
          </div>
          <div className="lg:col-span-8">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.98] font-light tracking-tight"
            >
              An institutional developer<br /><em className="text-primary italic">authoring landmarks.</em>
            </motion.h1>
          </div>
        </div>
      </section>

      {/* HERO IMAGE */}
      <section className="px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto aspect-[16/8] overflow-hidden">
          <img src={IMAGES.fitness_center} alt="Branded fitness center" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="overline mb-6">— Mission</div>
            <p className="font-serif text-2xl md:text-3xl leading-[1.25] font-light text-balance">
              Elliott International Development exists to design, capitalize and
              deliver <em className="text-primary italic">institutional-grade</em> mixed-use developments
              that elevate the cities they belong to.
            </p>
          </div>
          <div>
            <div className="overline mb-6">— Vision</div>
            <p className="font-serif text-2xl md:text-3xl leading-[1.25] font-light text-balance">
              To establish Tampa among the great American skyline cities — and
              to set the standard for ultra-luxury vertical urbanism across the
              Gulf Coast and beyond.
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-32 px-6 lg:px-12 bg-card border-y border-primary/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={IMAGES.founder_ceo_portrait} alt="Colton Elliott" className="w-full h-full object-cover" style={{ objectPosition: "center 25%" }} />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-6">— Founder & CEO</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] font-light tracking-tight">Colton Elliott</h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
              Elliott International Development was founded with a vision to
              create landmark developments that reshape skylines, elevate
              communities, and stand as generational assets. Led by founder and
              CEO Colton Elliott, the company is focused on high-impact real
              estate development with an emphasis on luxury, scale, design
              excellence, and long-term value creation.
            </p>
            <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
              What began as a lifelong dream to develop skyscrapers has evolved
              into a growing development platform with a pipeline centered
              around iconic mixed-use projects, luxury residences, hospitality,
              office, retail, and destination-driven real estate.
            </p>
            <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
              Elliott International Development is built on ambition,
              discipline, and the belief that extraordinary projects require
              bold vision, world-class execution, and the courage to build
              what others only imagine.
            </p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-6">
              <div>
                <div className="font-mono text-3xl text-primary">$1.1B</div>
                <div className="overline mt-1 text-foreground/50">Pipeline</div>
              </div>
              <div>
                <div className="font-mono text-3xl text-primary">740 ft</div>
                <div className="overline mt-1 text-foreground/50">Tampa's Tallest</div>
              </div>
              <div>
                <div className="font-mono text-3xl text-primary">Tampa</div>
                <div className="overline mt-1 text-foreground/50">Headquarters</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-32 lg:py-44 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          <SectionHeading overline="— Core values" title="Four principles guide every decision." />
          <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="glass p-8 h-full"
              >
                <div className="font-mono text-primary text-sm tracking-widest">{v.n}.</div>
                <h3 className="mt-6 font-serif text-2xl tracking-tight">{v.t}</h3>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{v.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-32 lg:py-44 px-6 lg:px-12 bg-card border-t border-primary/10">
        <div className="max-w-[1600px] mx-auto">
          <SectionHeading overline="— Trajectory" title="A vision in motion." />
          <div className="mt-20 relative">
            <div className="absolute left-0 lg:left-1/2 top-0 bottom-0 w-px bg-primary/20" />
            <div className="space-y-16">
              {TIMELINE.map((t, i) => (
                <motion.div
                  key={t.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className={`relative pl-8 lg:pl-0 grid lg:grid-cols-2 lg:gap-12 ${i % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
                >
                  <div className="absolute left-[-5px] lg:left-1/2 lg:-translate-x-1/2 top-2 w-[10px] h-[10px] rounded-full bg-primary" />
                  <div className={`${i % 2 === 1 ? "lg:order-2 lg:text-left" : "lg:text-right"}`}>
                    <div className="font-mono text-primary text-2xl tracking-tight">{t.year}</div>
                  </div>
                  <div className={`${i % 2 === 1 ? "lg:order-1" : ""}`}>
                    <h3 className="font-serif text-2xl">{t.title}</h3>
                    <p className="mt-3 text-muted-foreground leading-relaxed">{t.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
